import React, { useEffect } from 'react';
import { useGamingStore } from '../../store/gamingStore';
import {
  ArrowLeft, Play, Settings, Package, Palette, Sun, Map, Info,
  Loader2, Server
} from 'lucide-react';
import ProfileModsTab from './tabs/ProfileModsTab';
import ProfileResourcePacksTab from './tabs/ProfileResourcePacksTab';
import ProfileShadersTab from './tabs/ProfileShadersTab';
import ProfileWorldsTab from './tabs/ProfileWorldsTab';
import ProfileSettingsTab from './tabs/ProfileSettingsTab';

const LOADER_COLORS: Record<string, string> = {
  vanilla: 'text-emerald-400',
  forge: 'text-orange-400',
  fabric: 'text-blue-400',
  quilt: 'text-purple-400',
};

const SUB_TABS = [
  { id: 'overview' as const, label: 'Aperçu', icon: Info },
  { id: 'mods' as const, label: 'Mods', icon: Package },
  { id: 'resource-packs' as const, label: 'Resource Packs', icon: Palette },
  { id: 'shaders' as const, label: 'Shaders', icon: Sun },
  { id: 'worlds' as const, label: 'Mondes', icon: Map },
  { id: 'settings' as const, label: 'Paramètres', icon: Settings },
];

const ProfileDetail: React.FC = () => {
  const {
    selectedProfile: profile,
    profileSubTab,
    setProfileSubTab,
    closeProfile,
    launchProfile,
    isLaunchingProfile,
    launchProgress,
    fetchProfiles,
  } = useGamingStore();

  useEffect(() => {
    // Refresh profile data when we open the detail view
    fetchProfiles();
  }, [fetchProfiles]);

  if (!profile) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gaming-accent animate-spin" />
      </div>
    );
  }

  const isLaunching = isLaunchingProfile === profile.id;
  const modCount = profile.mods?.length || 0;
  const rpCount = profile.resourcePacks?.length || 0;
  const shaderCount = profile.shaderPacks?.length || 0;

  const renderTabContent = () => {
    switch (profileSubTab) {
      case 'mods':
        return <ProfileModsTab profile={profile} profileId={profile.id} />;
      case 'resource-packs':
        return <ProfileResourcePacksTab profile={profile} />;
      case 'shaders':
        return <ProfileShadersTab profile={profile} />;
      case 'worlds':
        return <ProfileWorldsTab profile={profile} />;
      case 'settings':
        return <ProfileSettingsTab profile={profile} />;
      case 'overview':
      default:
        return <ProfileOverview profile={profile} />;
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-color bg-bg-secondary/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          {/* Top bar */}
          <div className="flex items-center justify-between pt-4 pb-2">
            <div className="flex items-center gap-4">
              <button
                onClick={closeProfile}
                className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Profils
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                  <span className="text-sm font-black text-emerald-400">
                    {profile.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-lg font-bold text-text-primary leading-tight">{profile.name}</h1>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-text-secondary">{profile.minecraft.version}</span>
                    <span className={`font-semibold capitalize ${LOADER_COLORS[profile.minecraft.loader] || 'text-emerald-400'}`}>
                      {profile.minecraft.loader}
                    </span>
                    {profile.source === 'server' && (
                      <span className="text-gaming-accent flex items-center gap-0.5">
                        <Server className="w-3 h-3" /> Serveur
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Launch button */}
            <button
              onClick={() => launchProfile(profile.id)}
              disabled={isLaunching}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gaming-accent text-white font-medium text-sm hover:bg-gaming-accent-hover transition-colors disabled:opacity-50"
            >
              {isLaunching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4 fill-current" />
              )}
              {isLaunching
                ? (launchProgress?.status === 'error' ? 'Erreur' : (launchProgress?.message || 'Lancement...'))
                : 'Jouer'}
            </button>
          </div>

          {/* Launch progress bar */}
          {isLaunching && launchProgress && launchProgress.status !== 'error' && typeof launchProgress.progress === 'number' && (
            <div className="pb-2 pt-1">
              <div className="flex items-center justify-between text-[10px] text-text-muted mb-1">
                <span className="truncate max-w-[80%]">{launchProgress.message}</span>
                <span>{launchProgress.progress}%</span>
              </div>
              <div className="h-1 bg-bg-primary rounded-full overflow-hidden">
                <div
                  className="h-full bg-gaming-accent transition-all duration-300 rounded-full"
                  style={{ width: `${launchProgress.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Crash / error log */}
          {isLaunching && launchProgress?.status === 'error' && (
            <div className="pb-2 pt-1">
              <div className="text-[10px] text-red-400 font-semibold mb-1">Erreur de lancement</div>
              <pre className="text-[9px] text-red-300 bg-bg-primary rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all gaming-scrollbar">
                {launchProgress.message}
              </pre>
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-0 -mb-px gaming-scrollbar">
            {SUB_TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = profileSubTab === tab.id;
              let count: number | null = null;
              if (tab.id === 'mods') count = modCount;
              if (tab.id === 'resource-packs') count = rpCount;
              if (tab.id === 'shaders') count = shaderCount;

              return (
                <button
                  key={tab.id}
                  onClick={() => setProfileSubTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${
                    isActive
                      ? 'border-gaming-accent text-gaming-accent'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-color'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                  {count !== null && count > 0 && (
                    <span className={`ml-0.5 text-[10px] px-1 py-0.5 rounded-full ${
                      isActive ? 'bg-gaming-accent/20' : 'bg-bg-tertiary'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto gaming-scrollbar">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Profile Overview sub-tab
const ProfileOverview: React.FC<{ profile: any }> = ({ profile }) => {
  const { launchProfile, isLaunchingProfile } = useGamingStore();
  const isLaunching = isLaunchingProfile === profile.id;
  const isServerProfile = profile.source === 'server';
  const hasServerHero = isServerProfile && (profile.banner_url || profile.video_background_url);
  const visibleMods = Array.isArray(profile.mods) ? profile.mods.slice(0, 10) : [];

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      {hasServerHero && (
        <div className="relative mb-6 h-48 md:h-56 rounded-2xl overflow-hidden border border-border-color bg-bg-secondary">
          {profile.banner_url ? (
            <img
              src={profile.banner_url}
              alt={`Bannière ${profile.name}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <video
              src={profile.video_background_url}
              className="w-full h-full object-cover"
              muted
              loop
              autoPlay
              playsInline
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <p className="text-[10px] tracking-wide uppercase text-white/80 mb-1">Profil serveur</p>
            <h2 className="text-lg md:text-xl font-black text-white truncate">{profile.name}</h2>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left - Info */}
        <div className="md:col-span-2 space-y-4">
          {/* Server info */}
          {profile.server?.address && (
            <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                <Server className="w-4 h-4 text-gaming-accent" />
                Serveur
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-text-muted text-xs">Adresse</span>
                  <p className="text-text-primary font-mono text-xs">{profile.server.address}:{profile.server.port}</p>
                </div>
                <div>
                  <span className="text-text-muted text-xs">Auto-connect</span>
                  <p className="text-text-primary text-xs">{profile.server.auto_connect ? 'Oui' : 'Non'}</p>
                </div>
              </div>

              {isServerProfile && profile.server_description && (
                <div className="mt-3 pt-3 border-t border-border-color/70">
                  <span className="text-text-muted text-xs">Description</span>
                  <p className="text-text-primary text-sm mt-1 leading-relaxed whitespace-pre-wrap">
                    {profile.server_description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Minecraft config */}
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-2">Configuration</h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-text-muted text-xs">Version</span>
                <p className="text-text-primary text-xs font-medium">{profile.minecraft.version}</p>
              </div>
              <div>
                <span className="text-text-muted text-xs">Loader</span>
                <p className={`text-xs font-medium capitalize ${LOADER_COLORS[profile.minecraft.loader] || 'text-text-primary'}`}>
                  {profile.minecraft.loader}
                </p>
              </div>
              {profile.minecraft.loader_version && (
                <div>
                  <span className="text-text-muted text-xs">Version Loader</span>
                  <p className="text-text-primary text-xs">{profile.minecraft.loader_version}</p>
                </div>
              )}
            </div>
          </div>

          {/* Content summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Mods', count: profile.mods?.length || 0, icon: Package, color: 'text-blue-400' },
              { label: 'Resource Packs', count: profile.resourcePacks?.length || 0, icon: Palette, color: 'text-purple-400' },
              { label: 'Shaders', count: profile.shaderPacks?.length || 0, icon: Sun, color: 'text-yellow-400' },
            ].map(item => (
              <div key={item.label} className="bg-bg-secondary border border-border-color rounded-xl p-3 text-center">
                <item.icon className={`w-5 h-5 mx-auto mb-1 ${item.color}`} />
                <p className="text-lg font-bold text-text-primary">{item.count}</p>
                <p className="text-[10px] text-text-muted">{item.label}</p>
              </div>
            ))}
          </div>

          {/* Mods list */}
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-text-primary">Mods du profil</h3>
              <span className="text-[10px] text-text-muted">{profile.mods?.length || 0} total</span>
            </div>

            {visibleMods.length === 0 ? (
              <p className="text-xs text-text-muted">Aucun mod installé sur ce profil.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1 gaming-scrollbar">
                {visibleMods.map((mod: any, index: number) => {
                  const firstLetter = (mod.name || '?').charAt(0).toUpperCase();
                  return (
                    <div
                      key={`${mod.sha1 || mod.file_name || mod.name || 'mod'}-${index}`}
                      className="flex items-center gap-3 rounded-lg border border-border-color/70 bg-bg-primary/40 px-3 py-2"
                    >
                      {mod.icon_url ? (
                        <img
                          src={mod.icon_url}
                          alt={mod.name || 'Mod'}
                          className="w-9 h-9 rounded-md object-cover border border-border-color"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-md border border-border-color bg-bg-tertiary flex items-center justify-center">
                          <span className="text-xs font-bold text-text-secondary">{firstLetter}</span>
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-text-primary font-medium truncate">{mod.name || mod.file_name || 'Mod'}</p>
                        <div className="flex items-center gap-2 text-[10px] text-text-muted">
                          <span className="uppercase">{mod.source || 'custom'}</span>
                          {mod.version_id && <span className="truncate">{mod.version_id}</span>}
                        </div>
                      </div>

                      <span className={`text-[10px] px-2 py-1 rounded-full ${mod.required ? 'bg-red-500/10 text-red-300' : 'bg-emerald-500/10 text-emerald-300'}`}>
                        {mod.required ? 'Requis' : 'Optionnel'}
                      </span>
                    </div>
                  );
                })}
                {(profile.mods?.length || 0) > visibleMods.length && (
                  <p className="text-[10px] text-text-muted text-center pt-1">
                    +{(profile.mods?.length || 0) - visibleMods.length} autres mods dans l’onglet Mods
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right - Quick actions */}
        <div className="space-y-4">
          <button
            onClick={() => launchProfile(profile.id)}
            disabled={isLaunching}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gaming-accent text-white font-bold text-sm hover:bg-gaming-accent-hover transition-colors disabled:opacity-50"
          >
            {isLaunching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Play className="w-5 h-5 fill-current" />
            )}
            {isLaunching ? 'Lancement...' : 'Jouer'}
          </button>

          {/* Profile meta */}
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-text-muted">Créé</span>
              <span className="text-text-secondary">
                {new Date(profile.created_at).toLocaleDateString('fr-CA')}
              </span>
            </div>
            {profile.last_played_at && (
              <div className="flex justify-between">
                <span className="text-text-muted">Dernière partie</span>
                <span className="text-text-secondary">
                  {new Date(profile.last_played_at).toLocaleDateString('fr-CA')}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-muted">Manifest v.</span>
              <span className="text-text-secondary">{profile.manifest_version}</span>
            </div>
            {profile.last_synced_at && (
              <div className="flex justify-between">
                <span className="text-text-muted">Dernière sync</span>
                <span className="text-text-secondary">
                  {new Date(profile.last_synced_at).toLocaleDateString('fr-CA')}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-muted">Mods joueur</span>
              <span className={profile.allow_user_mods ? 'text-emerald-400' : 'text-red-400'}>
                {profile.allow_user_mods ? 'Autorisé' : 'Interdit'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
